import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Building2, Plus, Users } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function GroupSessionsPage() {
  const { user, axiosAuth } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', time: '', description: '', max_participants: 20 });
  const api = axiosAuth();
  const canCreate = ['peer_specialist', 'coordinator', 'admin'].includes(user.role);

  const load = () => api.get('/group-sessions').then(r => setSessions(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.title || !form.date) return toast.error('Title and date required');
    try {
      await api.post('/group-sessions', form);
      toast.success('Group session created!');
      setOpen(false);
      setForm({ title: '', date: '', time: '', description: '', max_participants: 20 });
      load();
    } catch { toast.error('Failed to create session'); }
  };

  const handleJoin = async (id) => {
    try {
      await api.post(`/group-sessions/${id}/join`);
      toast.success('Joined session!');
      load();
    } catch { toast.error('Failed to join'); }
  };

  return (
    <div className="space-y-6" data-testid="group-sessions-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Group Sessions</h1><p className="text-muted-foreground text-sm mt-1">Manage and join group activities</p></div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-group-session-button"><Plus size={16} /> New Session</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Create Group Session</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Session title" data-testid="group-title-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label>Time</Label><Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} /></div>
                </div>
                <div className="space-y-1.5"><Label>Description</Label><DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} rows={2} showCleanup={false} /></div>
                <div className="space-y-1.5"><Label>Max Participants</Label><Input type="number" value={form.max_participants} onChange={e => setForm({...form, max_participants: parseInt(e.target.value)})} /></div>
                <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="create-group-session-button">Create Session</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {sessions.length > 0 ? sessions.map(s => (
          <Card key={s.id} className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0"><Building2 className="w-6 h-6 text-violet-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{s.title}</p>
                <p className="text-sm text-muted-foreground">{s.date} at {s.time} &middot; Led by {s.facilitator_name}</p>
                {s.description && <p className="text-xs text-muted-foreground mt-1">{s.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-sm text-muted-foreground"><Users size={14} />{s.participants?.length || 0}/{s.max_participants}</div>
                <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => handleJoin(s.id)} data-testid={`join-session-${s.id}`}>Join</Button>
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center"><Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No group sessions yet.</p></CardContent></Card>
        )}
      </div>
    </div>
  );
}
